import type { OldButtonProps } from '../button/converter.old';
import { convertOldButtonProps } from '../button/converter.old';
import type { NotificationKind } from '../notification/types.old';
import type { AlertModalProps } from './createModalElement';
import type { ConfirmModalProps } from './createModalElement';
import type { FloatingModalProps } from './FloatingModal';
import type { ModalProps } from './Modal';
import type { NoticeModalProps } from './NoticeModal';

export type OldModalButtonProps = Omit<OldButtonProps, 'onClick'>;

interface OldOverlayProps {
  /** @deprecated open을 대신 사용해주세요. */
  opened?: boolean;
  /** @deprecated shouldCloseOnOutsideClick을 대신 사용해주세요. */
  canClickOutside?: boolean;
  /** @deprecated shouldCloseOnEscapeKey를 대신 사용해주세요. */
  canCloseEscapeKey?: boolean;
}

export interface OldModalProps
  extends Omit<
      ModalProps,
      | 'open'
      | 'shouldCloseOnOutsideClick'
      | 'shouldCloseOnEscapeKey'
      | 'closable'
      | 'onBack'
      | 'confirmButtonProps'
      | 'cancelButtonProps'
    >,
    OldOverlayProps {
  /** @deprecated closable을 대신 사용해주세요. */
  closeButton?: boolean;
  /** @deprecated onBack을 대신 사용해주세요. */
  onClickBackButton?: () => void;
  confirmButtonProps?: OldModalButtonProps;
  cancelButtonProps?: OldModalButtonProps;
}

export function convertOldModalProps(props: OldModalProps): ModalProps {
  const {
    opened,
    canClickOutside,
    canCloseEscapeKey,
    closeButton,
    onClickBackButton,
    confirmButtonProps,
    cancelButtonProps,
    ...restProps
  } = props;

  return {
    ...restProps,
    open: opened,
    shouldCloseOnOutsideClick: canClickOutside,
    shouldCloseOnEscapeKey: canCloseEscapeKey,
    closable: closeButton,
    onBack: onClickBackButton,
    confirmButtonProps: confirmButtonProps ? convertOldButtonProps(confirmButtonProps) : undefined,
    cancelButtonProps: cancelButtonProps ? convertOldButtonProps(cancelButtonProps) : undefined,
  };
}

export interface OldAlertModalProps
  extends Omit<
      AlertModalProps,
      | 'open'
      | 'shouldCloseOnOutsideClick'
      | 'shouldCloseOnEscapeKey'
      | 'status'
      | 'content'
      | 'description'
      | 'confirmButtonProps'
    >,
    OldOverlayProps {
  /** @deprecated status를 대신 사용해주세요. */
  kind?: NotificationKind;
  /** @deprecated content를 대신 사용해주세요. */
  text?: AlertModalProps['content'];
  /** @deprecated description을 대신 사용해주세요. */
  subtext?: AlertModalProps['description'];
  confirmButtonProps?: OldModalButtonProps;
  /** @internal 내부 사용을 위한 floatingPortalId */
  __floatingPortalId?: string;
}

export function convertOldAlertModalProps(props: OldAlertModalProps): AlertModalProps {
  const { opened, canClickOutside, canCloseEscapeKey, kind, text, subtext, confirmButtonProps, ...restProps } = props;

  return {
    ...restProps,
    open: opened,
    shouldCloseOnOutsideClick: canClickOutside,
    shouldCloseOnEscapeKey: canCloseEscapeKey,
    status: kind,
    content: text,
    description: subtext,
    confirmButtonProps: confirmButtonProps ? convertOldButtonProps(confirmButtonProps) : undefined,
  };
}

export function convertOldAlertProps(props: OldAlertModalProps): AlertModalProps {
  const convertedProps = convertOldAlertModalProps(props);

  if (props.opened !== undefined) return convertedProps;

  const { open: _open, ...uncontrolledProps } = convertedProps;
  return uncontrolledProps;
}

export interface OldConfirmModalProps extends Omit<OldAlertModalProps, 'autoFocusButton'> {
  cancelText?: string;
  onCancel?: () => void | Promise<void>;
  cancelButtonProps?: OldModalButtonProps;
  autoFocusButton?: ConfirmModalProps['autoFocusButton'];
}

export function convertOldConfirmModalProps(props: OldConfirmModalProps): ConfirmModalProps {
  const { cancelButtonProps, autoFocusButton, ...restProps } = props;

  return {
    ...convertOldAlertModalProps(restProps),
    cancelButtonProps: cancelButtonProps ? convertOldButtonProps(cancelButtonProps) : undefined,
    autoFocusButton,
  };
}

export function convertOldConfirmProps(props: OldConfirmModalProps): ConfirmModalProps {
  const convertedProps = convertOldConfirmModalProps(props);

  if (props.opened !== undefined) return convertedProps;

  const { open: _open, ...uncontrolledProps } = convertedProps;
  return uncontrolledProps;
}

export interface OldFloatingModalProps
  extends Omit<
      FloatingModalProps,
      | 'open'
      | 'shouldCloseOnOutsideClick'
      | 'shouldCloseOnEscapeKey'
      | 'closable'
      | 'onBack'
      | 'confirmButtonProps'
      | 'cancelButtonProps'
    >,
    OldOverlayProps {
  /** @deprecated closable을 대신 사용해주세요. */
  closeButton?: boolean;
  /** @deprecated onBack을 대신 사용해주세요. */
  onClickBackButton?: () => void;
  confirmButtonProps?: OldModalButtonProps;
  cancelButtonProps?: OldModalButtonProps;
}

export function convertOldFloatingModalProps(props: OldFloatingModalProps): FloatingModalProps {
  const {
    opened,
    canClickOutside,
    canCloseEscapeKey,
    closeButton,
    onClickBackButton,
    confirmButtonProps,
    cancelButtonProps,
    ...restProps
  } = props;

  return {
    ...restProps,
    open: opened ?? false,
    shouldCloseOnOutsideClick: canClickOutside,
    shouldCloseOnEscapeKey: canCloseEscapeKey,
    closable: closeButton,
    onBack: onClickBackButton,
    confirmButtonProps: confirmButtonProps ? convertOldButtonProps(confirmButtonProps) : undefined,
    cancelButtonProps: cancelButtonProps ? convertOldButtonProps(cancelButtonProps) : undefined,
  };
}

export interface OldNoticeModalProps
  extends Omit<
      NoticeModalProps,
      | 'open'
      | 'shouldCloseOnOutsideClick'
      | 'shouldCloseOnEscapeKey'
      | 'description'
      | 'checked'
      | 'checkboxLabel'
      | 'onCheckedChange'
    >,
    OldOverlayProps {
  /** @deprecated description을 대신 사용해주세요. */
  text?: NoticeModalProps['description'];
  /** @deprecated checked를 대신 사용해주세요. */
  checkReplay?: boolean;
  /** @deprecated checkboxLabel을 대신 사용해주세요. */
  checkReplayText?: string;
  /** @deprecated onCheckedChange를 대신 사용해주세요. */
  onChangeCheckReplay?: NoticeModalProps['onCheckedChange'];
}

export function convertOldNoticeModalProps(props: OldNoticeModalProps): NoticeModalProps {
  const {
    opened,
    canClickOutside,
    canCloseEscapeKey,
    text,
    checkReplay,
    checkReplayText,
    onChangeCheckReplay,
    ...restProps
  } = props;

  return {
    ...restProps,
    open: opened,
    shouldCloseOnOutsideClick: canClickOutside,
    shouldCloseOnEscapeKey: canCloseEscapeKey,
    description: text,
    checked: checkReplay,
    checkboxLabel: checkReplayText,
    onCheckedChange: onChangeCheckReplay,
  };
}
